import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import Axios from "axios";
import MaterialTable from "material-table";
import moment from "moment-timezone";

import { calculateTargetProd } from "../../../../util/targetCalc";
import { months } from "../../../../util/month.js";
import { toTitleCase, useFormatter } from "../../../../util/formatter";
import GYAxios from "../../../../util/GYAxios";

import DefaultSelect from "../../../general/DefaultSelect";

const TargetViewContent = (props) => {
	const [selectedSite, setSelectedSite] = useState(props.selectedSite);
	const [data, setData] = useState(null);
	const [years, setYears] = useState(null);
	const [prodIndex, setProdIndex] = useState(0);
	const [fileName, setFileName] = useState("");
	const [param, setParam] = useState(null);
	const [unused, setUnused] = useState(null);
	const [egrid, setEgrid] = useState(null);
	const reload = props.reload;
	const setReload = props.setReload;

	const [formatNumber] = useFormatter();

	const opts = [
		{ label: "BP", value: 0 },
		{ label: "Asbuilt", value: 1 },
	];

	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);

	/**
	 * Fetch target data
	 * @param {obj} site
	 */
	const fetchData = (site) => {
		const siteID = site.site_id;
		const contractDuration = site.contract_duration;
		const filterStr = `filter?column=id&value=${siteID}`;

		setYears(contractDuration);

		// API request to get target data
		const paramReq = GYAxios.get(`/target/parameters/${filterStr}`);
		const unusedReq = GYAxios.get(`/target/unused/${filterStr}`);
		const egridReq = GYAxios.get(`/target/egrid/${filterStr}`);

		const reqArr = [paramReq, unusedReq, egridReq];

		Axios.all(reqArr).then(
			Axios.spread((...responses) => {
				const paramRes = responses[0];
				const unusedRes = responses[1];
				const egridRes = responses[2];

				const paramObj = paramRes.error == null ? paramRes.data[0] : null;
				const unusedObj = unusedRes.error == null ? unusedRes.data[0] : null;
				const egridObj = egridRes.error == null ? egridRes.data[0] : null;

				setParam(paramObj);
				setUnused(unusedObj);
				setEgrid(egridObj);
			})
		);
	};

	/**
	 * Build target table
	 * @param {obj} data
	 * @param {obj} cod
	 * @param {string} years
	 */
	const buildTable = (data) => {
		// Get years for the table column
		const tempYears = data.map((yearArray) => getYear(yearArray));

		// Group elements from all sub arrays in all month array into single array
		const flattenArray = tempYears.flat();
		const uniqueFlattenYears = [...new Set(flattenArray)];

		// Columns
		const columns = buildColumns(uniqueFlattenYears);
		setColumns(columns);

		// Rows
		const row = buildRows(data);
		setRows(row);
	};

	/**
	 * Build table columns
	 * @param {string} YYYY
	 * @param {number} years
	 * @returns table columns
	 */
	const buildColumns = (years) => {
		const width = 150;
		const monthColumn = [{ title: "Month", field: "month", width }];

		const yearColumns = years.map((year) => ({
			title: `${year}`,
			field: `${year}`,
			width,
		}));

		const finalTitles = [...monthColumn, ...yearColumns];

		return finalTitles;
	};

	/**
	 * Build table rows
	 * @param {string} YYYY
	 * @param {obj} data
	 * @returns table rows
	 */
	const buildRows = (data) => {
		// Get base months array format
		const monthsArray = months.map((month) => ({
			month: toTitleCase(month),
			refMonth: month,
		}));

		// Group elements from all sub arrays in all month array into single array
		const allMonths = data.flat();

		// Group all months of every year into 12 month arrays
		const sortedMonthData = monthsArray.map((monthObj) => {
			const monthStr = monthObj.month;
			const refMonth = monthObj.refMonth;

			const filteredMonths = allMonths.filter(
				(singleMonth) => singleMonth.month === refMonth
			);

			const formattedEntries = filteredMonths.map((month) => [
				`${month.year}`,
				formatNumber(month.monthlyProd, 2),
			]);

			formattedEntries.push(["month", monthStr]);
			const formattedObj = Object.fromEntries(formattedEntries);

			return formattedObj;
		});

		return sortedMonthData;
	};

	/**
	 * Get years for the columns of the table
	 * @param {array} yearArray
	 * @returns
	 */
	const getYear = (yearArray) => {
		const years = yearArray.map((row) => row.year);
		const uniqueYears = [...new Set(years)];

		return uniqueYears;
	};

	useEffect(() => {
		if (selectedSite.site_id != props.selectedSite.site_id)
			setSelectedSite(props.selectedSite);
	}, [props.selectedSite]);

	useEffect(() => {
		setEgrid(null);
		setUnused(null);
		setParam(null);
		fetchData(selectedSite);
	}, [selectedSite, reload]);

	useEffect(() => {
		if (param != null && unused != null && egrid != null && years != null) {
			const capacity = selectedSite.power;
			const cod = selectedSite.cod;
			const siteCode =
				selectedSite.site_code != null ? selectedSite.site_code : "-";

			const paramData = param;
			const yearlyDecrement = paramData.degradation;

			const summedCorr =
				paramData.TA_correction +
				paramData.GA_correction +
				paramData.other_corrections;

			// Calculate Expected Production
			const prod = calculateTargetProd(
				egrid,
				capacity,
				summedCorr,
				unused,
				yearlyDecrement,
				cod,
				years
			);

			const fileName = `${opts[prodIndex].label} Prod-${siteCode}`;

			setFileName(fileName);
			setData(prod[prodIndex]);
			setReload(false);
		}
		return () => setData(null);
	}, [param, unused, egrid, years, prodIndex]);

	useEffect(() => {
		if (data != null) buildTable(data);
		return () => setData(null);
	}, [data]);

	return (
		<div>
			<Row style={{ margin: "10px 0" }}>
				<Col md="7">
					<h6>
						COD: <b>{moment(selectedSite.cod).format("DD MMM YYYY")}</b>
					</h6>
				</Col>
				<Col md="3" style={{ textAlign: "right" }}>
					<h6>Type of Expected Production: </h6>
				</Col>
				<Col md="2">
					<DefaultSelect
						options={opts}
						defaultValue={opts[0]}
						onChange={(opt) => setProdIndex(opt.value)}
						customStyle={{ width: "100%" }}
					/>
				</Col>
			</Row>
			<Row>
				<Col md="12">
					<MaterialTable
						columns={columns}
						data={rows}
						isLoading={reload}
						options={{
							showTitle: false,
							exportButton: true,
							exportAllData: true,
							sorting: false,
							pageSize: 12,
							pageSizeOptions: [12],
							headerStyle: {
								fontSize: "1rem",
								textAlign: "center",
							},
							cellStyle: {
								textAlign: "center",
							},
							fixedColumns: {
								left: 1,
							},
							exportFileName: fileName,
						}}
					/>
				</Col>
			</Row>
		</div>
	);
};

export default TargetViewContent;
